import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {RowLR, Spinner, Text} from "react-vcomponents";
import {TextPlus} from "web-vcore";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {Mod_TerrainSpotHeight} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainSpotHeight.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {Vec2Editor} from "../../../../../Utils/ReactComponents/VecEditor.js";

const splitAt = 180;

/** V3.1 (plan §5.3.3): editor for `Mod_TerrainSpotHeight` — a point-shaped terrain feature
 *  that pins a small Dirichlet disk at the parent `MapObject`'s anchor position. Mirrors the
 *  pattern in `TerrainDefinition_ModUI.tsx` / `TerrainRegion_ModUI.tsx`: just an enabled
 *  checkbox (driven by `Mod.enabled` in `Modifiers_CompUI`'s shared header) + elevation +
 *  radius spinners. The rasterizer bridge (`collectRasterFeatures` in
 *  `Poisson/ConstraintRasterizer.ts`) converts this into a `SpotHeightFeature` at solve-prep
 *  time; no solver-side wiring is needed here.
 *
 *  Note: the modifier-level enabled checkbox is provided by `Modifiers_CompUI.ModifierEditorUI`
 *  (the shared headerClone / move-up / move-down / delete row) — this panel surfaces only the
 *  spot-height-specific knobs. */
export const TerrainSpotHeight_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper: ChangeMod} = props;
	const Change = (mutator: (data: Mod_TerrainSpotHeight)=>any)=>ChangeMod(a=>mutator(a.terrainSpotHeight!));
	const data = mod.terrainSpotHeight!;

	return (
		<>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Target elevation (meters above sea level, world frame). Every AMR leaf cell whose center lies
					within \`radius\` metres of this MapObject's anchor position is pinned at this elevation
					(§6.6 spot-height rasterization) — a Dirichlet disk in the Poisson solve.
				`.AsMultiline(0)}>Elevation (m):</TextPlus>
				<Spinner step={1} value={data.elevation} onChange={val=>Change(a=>a.elevation = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Pin radius (metres). Cells within this radius of the MapObject's anchor point are pinned at
					\`elevation\`. Default 0.5 = single-cell pin (the smallest practical pin at the default cell
					size). Larger values produce a wider Dirichlet disk — useful for pinning a flat hilltop or a
					plateau summit point with a known extent.
				`.AsMultiline(0)}>Radius (m):</TextPlus>
				<Spinner step={0.5} min={0} value={data.radius} onChange={val=>Change(a=>a.radius = Math.max(0, val))}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					World-position offset (metres) added to the MapObject's anchor position before rasterizing the
					spot-height disk. Rotation is not applied (consistent with ObjPath geometry). Mirrors the
					\`MapObject.label_offset\` UI.
				`.AsMultiline(0)}>Offset (m):</TextPlus>
				<Vec2Editor ml={5} value={data.offset} onChange={val=>Change(a=>a.offset = val)}/>
			</RowLR>
		</>
	);
});